/**
 * Email Normalization Utilities
 *
 * Handles email normalization and validation with environment-aware behavior:
 * - PRODUCTION: Blocks email aliases, returns lowercase email as-is
 * - DEVELOPMENT/UAT: Allows Gmail aliases for testing convenience
 *
 * This module is critical for security and prevents alias confusion in production
 * while enabling convenient testing workflows in development and UAT.
 */

/**
 * Detects if the application is running in development or UAT mode
 * (where email aliases are allowed for testing)
 *
 * @returns true if running in development or UAT, false if production
 */
export const isDevelopmentOrUAT = (): boolean => {
  return (
    import.meta.env.DEV ||
    import.meta.env.MODE === 'development' ||
    import.meta.env.VITE_UAT_MODE === 'true' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname.includes('127.0.0.1')
  )
}

/**
 * @deprecated Use isDevelopmentOrUAT instead
 */
export const isDevelopment = isDevelopmentOrUAT

/**
 * Normalizes email addresses for comparison.
 *
 * PRODUCTION: Returns lowercase email as-is (no alias stripping)
 * DEVELOPMENT/UAT: Strips Gmail alias tags (e.g., user+test@gmail.com → user@gmail.com)
 *
 * This allows convenient testing in development/UAT with email aliases while
 * preventing alias confusion and security issues in production.
 *
 * @param email - Email address to normalize
 * @returns Normalized email address
 */
export function normalizeEmail(email: string): string {
  if (!email) return ''

  const lowercaseEmail = email.toLowerCase().trim()

  // PRODUCTION: No normalization, return as-is
  if (!isDevelopmentOrUAT()) {
    return lowercaseEmail
  }

  // DEVELOPMENT: Strip Gmail aliases for testing convenience
  const [localPart, domain] = lowercaseEmail.split('@')

  if (!domain) return lowercaseEmail

  // Only normalize gmail.com and googlemail.com
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    const cleanLocal = localPart.split('+')[0]
    return `${cleanLocal}@${domain}`
  }

  return lowercaseEmail
}

/**
 * Validates email for production use - blocks email aliases
 *
 * @param email - Email address to validate
 * @returns Object with validation result and optional error message
 */
export function validateEmailForProduction(email: string): {
  valid: boolean
  error?: string
} {
  // In development/UAT, all emails are valid (aliases allowed for testing)
  if (isDevelopmentOrUAT()) {
    return { valid: true }
  }

  // PRODUCTION: Block email aliases (containing +)
  if (email.includes('+')) {
    return {
      valid: false,
      error:
        'Email aliases (containing +) are not allowed. Please use the primary email address.',
    }
  }

  return { valid: true }
}

/**
 * Checks if two email addresses match after normalization
 *
 * @param email1 - First email address
 * @param email2 - Second email address
 * @returns true if emails match after normalization
 */
export function emailsMatch(email1: string, email2: string): boolean {
  return normalizeEmail(email1) === normalizeEmail(email2)
}
